import React, { useState } from "react";
import Breadcrumb from "../../../../Components/Common/Breadcrumb";
import Tabs from "../../../../Components/Common/Tabs";
import { tutoringPagesTabsData } from "../../../../data/SidebarData";
import { faAdd } from "@fortawesome/free-solid-svg-icons";
import Table from "../../../../Components/Common/Table";
import TableHead from "../../../../Components/Common/TableHead";
import useSWR from "swr";
import { packageUrls } from "../../../../Api/Auth/ApiUrls";
import QueryString from "qs";
import useTable from "../../../../hooks/useTable";
import { Loader } from "../../../../Components/Common/Loader";
import PackagesRow from "./Components/PackagesRow";
import NoData from "../../../../Components/Common/NoData";
import Pagination from "../../../../Components/Common/Pagination/Pagination";
import { useLocation } from "react-router-dom";
import useToastError from "../../../../hooks/useErrorToast";
import Modal from "../../../../Components/Common/Modal";
import PackageForm from "../TutoringPackages/Components/PackageForm";
import useSWRMutation from "swr/mutation";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../../Api/swrConfig";
import { toast } from "react-toastify";
import Storage from "../../Storage";
import { useAuth } from "../../../../Context/AuthContext";
import { one2OneTutoring } from "../../../../Api/Auth/AppUrls";

const tableColumnData = [
  { id: "title", label: "Title" },
  { id: "category", label: "Category" },
  { id: "price", label: "Price" },
  { id: "time", label: "Time" },
  { id: "createdAt", label: "Created" },
];

const TutoringPackages = () => {
  const [row, setRow] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    title: "",
    price: 0,
    time: "",
    image: "",
    category: null,
    actionButton: "",
  });
  const [isModalOpen, setModalOpen] = useState(false);
  const [isStorageModalOpen, setStorageModalOpen] = useState(false);
  const { showErrorToast } = useToastError();
  const location = useLocation();
  const { userData } = useAuth();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTable({ defaultOrderBy: "" });
  const apiQueryParams = {
    offset: (page - 1) * rowsPerPage,
    limit: rowsPerPage,
  };
  const apiQueryString = QueryString.stringify(apiQueryParams);
  const { isLoading, data, mutate } = useSWR(packageUrls.list(apiQueryString));

  const handleInputChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // Set only the field that's changed
    }));
  };
  const onEditClick = (row) => {
    setRow(row);
    handleInputChange("title", row.title);
    handleInputChange("price", row.price);
    handleInputChange("time", row.time);
    handleInputChange("category", row.category?._id);
    handleInputChange("actionButton", row.actionButton);
    handleInputChange("image", row.image);
    setModalOpen(true);
  };

  const { trigger, isMutating } = useSWRMutation(
    packageUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );
  const { trigger: EditTrigger, isMutating: editMutating } = useSWRMutation(
    packageUrls.edit(row?._id),
    sendPutRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const actionButtons = [
    {
      label: "Create",
      icon: faAdd,
      onClick: () => {
        setRow(null);
        setFormData({
          title: "",
          price: 0,
          time: "",
          image: "",
          category: null,
          actionButton: "",
        });
        setModalOpen(true);
      },
    },
  ];
  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.price) newErrors.price = "Price is required";
    if (!formData.time) newErrors.time = "Time is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    const modifiedData = {
      ...formData,
      category:
        typeof formData.category === "object"
          ? formData.category.value
          : formData.category,
    };

    row ? EditTrigger(modifiedData) : trigger(modifiedData);
  };

  return (
    <div>
      <Breadcrumb actionButtons={actionButtons} />
      <Tabs
        tabs={
          userData?.roleId === 6
            ? tutoringPagesTabsData.filter(
                (item) => item.path !== one2OneTutoring.purchase
              )
            : tutoringPagesTabsData
        }
      />
      <Table>
        <TableHead rowCount={data?.data.length} headerData={tableColumnData} />
        <tbody className={`divide-y divide-gray-200`}>
          {!isLoading ? (
            <>
              {data?.data.map((row) => (
                <PackagesRow
                  key={row._id}
                  row={row}
                  mutate={mutate}
                  onEditClick={onEditClick}
                />
              ))}
            </>
          ) : (
            <tr>
              <td colSpan="100" className="text-center py-4">
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
        <NoData isNotFound={!data?.data.length && !isLoading} />
      </Table>
      <Pagination
        totalRows={data?.pagination.totalItems || 0}
        currentPage={page}
        rowsPerPage={rowsPerPage}
        currentPath={location.pathname}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        isOpen={isModalOpen}
        title={row ? "Edit Package" : "Create Package"}
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setModalOpen(false);
        }}
        isLoading={isMutating || editMutating}
        formWidth
      >
        <PackageForm
          errors={errors}
          handleSubmit={handleSubmit}
          onChange={handleInputChange}
          formData={formData}
          setModalOpen={setStorageModalOpen}
        />
      </Modal>
      <Modal
        isOpen={isStorageModalOpen}
        title={"Storage Manager"}
        // onConfirm={handleSubmit}
        onCancel={() => {
          setStorageModalOpen(false);
        }}
        footer={false}
      >
        <Storage
          isSelector
          onConfirm={(file) => {
            if (Array.isArray(file) && file.length)
              handleInputChange("image", file[0]?.fileUrl);
            setStorageModalOpen(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default TutoringPackages;
